import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { DwComponent, DwComponentType } from '@devwareapps/devware-cap';
import { Observable, of, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ChallengeContextService } from '../shared/contexts/challenge-context/challenge-context.service';
import { StreakerCentralStateService } from './services/streaker-central-state.service';
import { CentralDataService } from './services/central-data.service';
import { CentralNavigationService } from './services/central-navigation.service';
import { CentralAdminAccessService } from './admin/central-admin-access.service';

@DwComponent({ key: 'fitness-streaker-central', name: 'Streaker Central', componentType: DwComponentType.pageComponent, isGlobal: true })
@Component({ selector: 'app-streaker-central', templateUrl: './streaker-central.component.html',
    styleUrls: ['./streaker-central.component.scss'], encapsulation: ViewEncapsulation.None,
    providers: [StreakerCentralStateService, CentralNavigationService] })
export class StreakerCentralComponent implements OnDestroy {
    private subscriptions = new Subscription();
    private child: { canNavigate?: () => Observable<boolean>; reloadProfile?: () => void };
    readonly allowChallengeChange = (): Observable<boolean> => this.child?.canNavigate ? this.child.canNavigate() : of(true);
    constructor(public state: StreakerCentralStateService, private router: Router, private route: ActivatedRoute,
        private selection: ChallengeContextService, private data: CentralDataService, public navigation:CentralNavigationService,
        public adminAccess: CentralAdminAccessService) {
        this.subscriptions.add(router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => this.syncRoute()));
    }
    childActivated(component: { canNavigate?: () => Observable<boolean>; reloadProfile?: () => void }): void { this.child = component; }
    challengeSelected(): void {
        const child = this.route.firstChild?.snapshot;
        if (child?.paramMap.has('slug') || child?.paramMap.has('teamId')) {
            const section = child.url[0]?.path === 'teams' ? 'teams' : 'streakers';
            this.router.navigate([section], { relativeTo: this.route, queryParams: { view: null, challengeId: null }, queryParamsHandling: 'merge' });
        } else {
            if (this.route.snapshot?.queryParamMap?.has('challengeId')) {
                this.router.navigate([], { relativeTo: this.route, queryParams: { challengeId: null }, queryParamsHandling: 'merge', replaceUrl: true });
            }
            this.child?.reloadProfile?.();
        }
    }
    private syncRoute(): void {
        // Angular exposes the child route before assigning its snapshot during activation.
        // Read it after NavigationEnd, and tolerate an outlet that has not activated yet.
        const child = this.route.firstChild?.snapshot;
        const parent = this.route.snapshot;
        if (!parent || !child) { return; }
        const routeChallengeId = () => Number(this.route.firstChild?.snapshot?.paramMap.get('challengeId'))
            || Number(this.route.snapshot?.queryParamMap?.get('challengeId'));
        const challengeId = routeChallengeId();
        if (Number.isSafeInteger(challengeId) && challengeId > 0 && challengeId !== this.selection.currentChallenge.ChallengeDefinitionId) {
            this.subscriptions.add(this.data.definitions().pipe(take(1)).subscribe(definitions => {
                if (routeChallengeId() !== challengeId) { return; }
                const challenge = definitions.find(row => row.ChallengeDefinitionId === challengeId);
                if (challenge) {
                    this.subscriptions.add(this.allowChallengeChange().pipe(take(1)).subscribe(allowed => {
                        if (allowed && routeChallengeId() === challengeId) {
                            this.selection.setChallenge({ ChallengeDefinitionId: challengeId, ChallengeName: challenge.Name });
                        }
                    }));
                }
            }, () => { /* Child pages own load errors. */ }));
        }
        const routeTeamId = () => {
            const params = this.route.firstChild?.snapshot?.paramMap;
            const slug = params?.get('slug');
            return Number(params?.get('teamId')) || (!params?.has('challengeId') && /^\d+$/.test(slug || '') ? Number(slug) : 0);
        };
        if (routeTeamId()) {
            const teamId = routeTeamId();
            if (Number.isSafeInteger(teamId) && teamId > 0) {
                this.subscriptions.add(this.data.team(teamId).pipe(take(1)).subscribe(teams => {
                    if (routeTeamId() !== teamId) { return; }
                    const team = teams[0];
                    if (team && team.ChallengeDefinitionId !== this.selection.currentChallenge.ChallengeDefinitionId) {
                        this.subscriptions.add(this.data.definitions().pipe(take(1)).subscribe(definitions => {
                            if (routeTeamId() !== teamId) { return; }
                            const challenge = definitions.find(row => row.ChallengeDefinitionId === team.ChallengeDefinitionId);
                            if (challenge) { this.selection.setChallenge({ ChallengeDefinitionId: challenge.ChallengeDefinitionId, ChallengeName: challenge.Name }); }
                        }, () => { /* Child pages own load errors. */ }));
                    }
                }, () => { /* The detail page owns its load error. */ }));
            }
        }
        const view = parent.queryParamMap.get('view');
        if (child.url.length === 0 && (view === 'teams' || view === 'stats')) {
            this.router.navigate([view], { relativeTo: this.route, queryParams: { view: null }, queryParamsHandling: 'merge', replaceUrl: true });
        }
    }
    ngOnDestroy(): void { this.subscriptions.unsubscribe(); }
}
